import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, CircleAlert, LoaderCircle, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Brand } from "../components/layout/Brand";
import { Button } from "../components/ui/Button";
import { confirmAccess, confirmationErrorMessage } from "../lib/api";

export function ConfirmAccessPage() {
  // The one-time token stays in the URL fragment, so it is not sent in the
  // initial HTTP request, Referer header or ordinary web-server access log.
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get("token") ?? "";
  const flow = params.get("flow") === "register" ? "register" : "recovery";
  const validToken = /^[a-f0-9]{32}$/.test(token);
  const confirmation = useQuery({
    queryKey: ["access-confirmation", token],
    queryFn: () => confirmAccess(token),
    enabled: validToken,
    retry: false,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });

  const successfulTitle = flow === "register" ? "Účet je aktivovaný" : "Přístup je obnovený";
  const successfulText = flow === "register"
    ? "Registrace je dokončená. Nyní se můžete přihlásit údaji, které jsme vám poslali e-mailem."
    : "Nové přihlašovací heslo a heslo pro komunikaci s podporou jsme vám poslali e-mailem.";
  const error = !validToken
    ? "Potvrzovací odkaz není platný nebo je neúplný. Použijte prosím celý odkaz z e-mailu."
    : confirmation.error
      ? confirmationErrorMessage(confirmation.error)
      : null;
  const isLoading = validToken && confirmation.isPending;

  return (
    <main className="auth-page">
      <section className="auth-aside">
        <Link to="/" className="auth-back">Zpět na hlavní stránku</Link>
        <div className="auth-aside__content">
          <Brand />
          <h1>Bezpečné potvrzení<br /><em>vašeho účtu.</em></h1>
          <p>Jednorázový odkaz dokončí pouze akci, o kterou jste požádali.</p>
          <ul><li><ShieldCheck /> Odkaz lze použít pouze jednou</li><li><ShieldCheck /> Bez analytických a marketingových cookies</li></ul>
        </div>
        <small>Cloudovou platformu provozuje Synesta s.r.o.</small>
      </section>
      <section className="auth-content">
        <div className="auth-mobile-brand"><Brand /></div>
        <div className="auth-card">
          <div className="auth-success" aria-live="polite">
            <span className={error ? "auth-result-icon--error" : undefined}>
              {error ? <CircleAlert /> : isLoading ? <LoaderCircle className="spinner-icon" /> : <CheckCircle2 />}
            </span>
            <h2>{error ? "Potvrzení se nezdařilo" : isLoading ? "Ověřujeme odkaz…" : successfulTitle}</h2>
            <p>{error ?? (isLoading ? "Vyčkejte prosím, dokončení obvykle trvá jen několik sekund." : successfulText)}</p>
            {!isLoading && (
              <Button variant="primary" onClick={() => { window.location.href = error ? "/auth/recovery" : "/auth/login"; }} icon={<ArrowRight />}>
                {error ? "Požádat o nový odkaz" : "Přejít k přihlášení"}
              </Button>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
